import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  NOTIFICATION_TYPE,
} from '../../../../../constants/common'
import { FILE_UPLOAD_DROPZONE_OPTIONS } from '../../../../File'
import {
  APPLICANT_CREDIT_CHECK_BAND,
  APPLICANT_STAGE,
  getApplicantFieldValue,
} from '../../../../RentalPortalApplicant'
import { getIsApplicantClosed } from '../../RentalPortalApplicantProfile.utils'
import { getApplicantShortDate } from '../../RentalPortalApplicantProfileHeaderInfoItems'

import {
  APPLICANT_CREDIT_CHECK_ACTION,
  APPLICANT_CREDIT_CHECK_GATE_MODE,
  APPLICANT_CREDIT_CHECK_PRIMARY_ACTIONS,
  APPLICANT_CREDIT_CHECK_REQUEST_ERROR_NOTIFICATION_ID,
  APPLICANT_CREDIT_CHECK_ROW,
  APPLICANT_CREDIT_CHECK_SENSITIVE_ACTIONS,
  APPLICANT_CREDIT_CHECK_STATUS,
  APPLICANT_CREDIT_CHECK_STATUS_ACTIONS,
  APPLICANT_CREDIT_CHECK_SUGGESTION_STATUS,
  APPLICANT_CREDIT_CHECK_UPLOAD_ACTIONS,
  APPLICANT_CREDIT_CHECK_UPLOAD_MAX_FILES,
} from './ApplicantCreditCheckBlock.constants'

function getApplicantCreditCheckDisabledTooltipKey(applicantData) {
  if (getIsApplicantClosed(applicantData)) {
    return 'RentalPortal.ApplicantCreditCheckClosedTooltipKey'
  }

  return applicantData?.permissions?.can_screen
    ? null
    : 'RentalPortal.ApplicantCreditCheckNotAllowedTooltipKey'
}

function getApplicantCreditCheckConsentRow({ creditCheck, t }) {
  return {
    key: APPLICANT_CREDIT_CHECK_ROW.consent,
    label: t('RentalPortal.ApplicantCreditCheckConsentLabelKey'),
    value: creditCheck?.consent?.given
      ? getApplicantShortDate(creditCheck.consent.given_at)
      : t('RentalPortal.ApplicantCreditCheckConsentOutstandingKey'),
  }
}

function getApplicantCreditCheckSuggestionsByStatus({ creditCheck, status }) {
  return (creditCheck?.suggestions ?? EMPTY_LIST)
    .filter(suggestion => suggestion.status === status)
    .sort((first, second) => second.arrived_at.localeCompare(first.arrived_at))
}

function getApplicantCreditCheckExpectedProviderKey(creditCheck) {
  return (
    creditCheck?.expected?.provider_key ??
    creditCheck?.consent?.provider_key ??
    null
  )
}

function getIsApplicantCreditCheckSuggestionCrossProvider({
  creditCheck,
  suggestion,
}) {
  const expectedProviderKey =
    getApplicantCreditCheckExpectedProviderKey(creditCheck)

  return (
    suggestion?.detected_provider_key &&
    expectedProviderKey &&
    suggestion.detected_provider_key !== expectedProviderKey
  )
}

function getIsApplicantCreditCheckRequestAction(action) {
  return action === APPLICANT_CREDIT_CHECK_ACTION.request
}

function getIsApplicantCreditCheckReminderAction(action) {
  return action === APPLICANT_CREDIT_CHECK_ACTION.reminder
}

export function getHasApplicantCreditCheck(applicantData) {
  return applicantData?.credit_check
}

export function getIsApplicantCreditCheckPreScreening(applicantData) {
  return (
    (applicantData?.phase?.key ?? applicantData?.stage) === APPLICANT_STAGE.pool
  )
}

export function getApplicantCreditCheckResults(creditCheck) {
  return creditCheck?.supplementary ?? EMPTY_LIST
}

export function getApplicantCreditCheckSatisfyingResult(creditCheck) {
  const providerKey = creditCheck?.readiness_satisfied_by

  return providerKey
    ? [
        creditCheck?.expected,
        ...getApplicantCreditCheckResults(creditCheck),
      ].find(result => result?.provider_key === providerKey)
    : null
}

export function getIsApplicantCreditCheckInconclusive(result) {
  return (
    getApplicantFieldValue(result?.normalised_band) ===
    APPLICANT_CREDIT_CHECK_BAND.inconclusive
  )
}

export function getApplicantCreditCheckVerdictResult(creditCheck) {
  return creditCheck?.expected ?? null
}

export function getHasApplicantCreditCheckDocument(creditCheck) {
  return getApplicantCreditCheckVerdictResult(creditCheck)?.document_id
}

export function getIsApplicantCreditCheckResultExpired(result) {
  return result?.is_expired
}

export function getApplicantCreditCheckConsentText({ creditCheck, t }) {
  return creditCheck?.consent?.given
    ? t('RentalPortal.ApplicantCreditCheckConsentGivenKey', {
        date: getApplicantShortDate(creditCheck.consent.given_at),
      })
    : t('RentalPortal.ApplicantCreditCheckConsentRequiredKey')
}

export function getApplicantCreditCheckConsentVersionText({ creditCheck, t }) {
  return creditCheck?.consent?.consent_text_version
    ? t('RentalPortal.ApplicantCreditCheckConsentVersionKey', {
        version: creditCheck.consent.consent_text_version,
      })
    : null
}

export function getApplicantCreditCheckNativeValueRow(result) {
  const value = getApplicantFieldValue(result?.native_value)

  return value && result?.native_scale_label
    ? { label: result.native_scale_label, value }
    : null
}

export function getApplicantCreditCheckDateRows({ result, t }) {
  const rows = []

  if (result?.issued_at) {
    rows.push({
      key: APPLICANT_CREDIT_CHECK_ROW.issuedAt,
      label: t('RentalPortal.ApplicantCreditCheckVerdictIssuedAtLabelKey'),
      value: getApplicantShortDate(result.issued_at),
    })
  }

  if (result?.expires_at) {
    rows.push({
      key: APPLICANT_CREDIT_CHECK_ROW.expiresAt,
      label: t(
        getIsApplicantCreditCheckResultExpired(result)
          ? 'RentalPortal.ApplicantCreditCheckExpiredAtLabelKey'
          : 'RentalPortal.ApplicantCreditCheckVerdictExpiresAtLabelKey'
      ),
      value: getApplicantShortDate(result.expires_at),
    })
  }

  return rows
}

export function getApplicantCreditCheckVerificationRows({ result, t }) {
  const isChecked = result?.verified_at && result?.verification_code

  const rows = [
    {
      key: APPLICANT_CREDIT_CHECK_ROW.verificationCode,
      label: t('RentalPortal.ApplicantCreditCheckVerificationCodeLabelKey'),
      value: isChecked
        ? result.verification_code
        : t('RentalPortal.ApplicantCreditCheckVerificationCodeNotCheckedKey'),
    },
  ]

  if (isChecked) {
    rows.push({
      key: APPLICANT_CREDIT_CHECK_ROW.verifiedAt,
      label: t('RentalPortal.ApplicantCreditCheckVerifiedAtLabelKey'),
      value: getApplicantShortDate(result.verified_at),
    })
  }

  return rows
}

export function getApplicantCreditCheckActionState({
  action,
  applicantData,
  isPreview,
}) {
  const creditCheck = applicantData?.credit_check
  const isSensitiveAction =
    APPLICANT_CREDIT_CHECK_SENSITIVE_ACTIONS.includes(action)

  const isStatusAction = (
    APPLICANT_CREDIT_CHECK_STATUS_ACTIONS[creditCheck?.status] ?? EMPTY_LIST
  ).includes(action)

  const isSensitiveActionDenied =
    isSensitiveAction && !applicantData?.permissions?.can_view_sensitive

  if (
    isPreview ||
    getIsApplicantCreditCheckPreScreening(applicantData) ||
    !isStatusAction ||
    isSensitiveActionDenied
  ) {
    return { isHidden: true }
  }

  const tooltipKey = getApplicantCreditCheckDisabledTooltipKey(applicantData)

  if (tooltipKey) {
    return { tooltipKey }
  }

  if (
    getIsApplicantCreditCheckRequestAction(action) &&
    !creditCheck?.consent?.given
  ) {
    return {
      tooltipKey: 'RentalPortal.ApplicantCreditCheckConsentMissingTooltipKey',
    }
  }

  return isSensitiveAction && !getHasApplicantCreditCheckDocument(creditCheck)
    ? {
        tooltipKey:
          'RentalPortal.ApplicantCreditCheckVerdictNoDocumentTooltipKey',
      }
    : EMPTY_OBJECT
}

export function getApplicantCreditCheckRows({ creditCheck, t }) {
  const satisfyingResult = getApplicantCreditCheckSatisfyingResult(creditCheck)

  const rows = [getApplicantCreditCheckConsentRow({ creditCheck, t })]

  if (creditCheck?.consent?.consent_text_version) {
    rows.push({
      key: APPLICANT_CREDIT_CHECK_ROW.consentVersion,
      label: t('RentalPortal.ApplicantCreditCheckConsentVersionLabelKey'),
      value: creditCheck.consent.consent_text_version,
    })
  }

  if (creditCheck?.requested_at) {
    rows.push({
      key: APPLICANT_CREDIT_CHECK_ROW.requestedAt,
      label: t('RentalPortal.ApplicantCreditCheckRequestedAtLabelKey'),
      value: getApplicantShortDate(creditCheck.requested_at),
    })
  }

  if (creditCheck?.reminded_at) {
    rows.push({
      key: APPLICANT_CREDIT_CHECK_ROW.remindedAt,
      label: t('RentalPortal.ApplicantCreditCheckRemindedAtLabelKey'),
      value: getApplicantShortDate(creditCheck.reminded_at),
    })
  }

  if (satisfyingResult) {
    rows.push({
      key: APPLICANT_CREDIT_CHECK_ROW.satisfiedBy,
      label: t('RentalPortal.ApplicantCreditCheckSatisfiedByLabelKey'),
      value: satisfyingResult.provider_display_name,
    })
  }

  return rows
}

export function getApplicantCreditCheckUploadOptions(isDisabled) {
  return {
    ...FILE_UPLOAD_DROPZONE_OPTIONS,
    disabled: isDisabled,
    maxFiles: APPLICANT_CREDIT_CHECK_UPLOAD_MAX_FILES,
    multiple: false,
  }
}

export function getApplicantCreditCheckGateMode(action) {
  return getIsApplicantCreditCheckReminderAction(action)
    ? APPLICANT_CREDIT_CHECK_GATE_MODE.reminder
    : APPLICANT_CREDIT_CHECK_GATE_MODE.request
}

export function getIsApplicantCreditCheckAwaiting(creditCheck) {
  return creditCheck?.status === APPLICANT_CREDIT_CHECK_STATUS.requested
}

export function getApplicantCreditCheckSuggestions(creditCheck) {
  return getApplicantCreditCheckSuggestionsByStatus({
    creditCheck,
    status: APPLICANT_CREDIT_CHECK_SUGGESTION_STATUS.pending,
  })
}

export function getApplicantCreditCheckDismissedTexts({ creditCheck, t }) {
  return getApplicantCreditCheckSuggestionsByStatus({
    creditCheck,
    status: APPLICANT_CREDIT_CHECK_SUGGESTION_STATUS.dismissed,
  })
    .filter(
      suggestion => suggestion.resolved_by?.full_name && suggestion.resolved_at
    )
    .map(suggestion => ({
      id: suggestion.id,
      text: t('RentalPortal.ApplicantCreditCheckSuggestionDismissedKey', {
        date: getApplicantShortDate(suggestion.resolved_at),
        name: suggestion.resolved_by.full_name,
      }),
    }))
}

export function getApplicantCreditCheckSuggestionActionsState({
  applicantData,
  isPreview,
  suggestion,
}) {
  if (
    isPreview ||
    getIsApplicantClosed(applicantData) ||
    getIsApplicantCreditCheckPreScreening(applicantData)
  ) {
    return { isDismissHidden: true, isReviewHidden: true }
  }

  return {
    isDismissHidden: false,
    isReviewHidden:
      !applicantData?.permissions?.can_view_sensitive ||
      getIsApplicantCreditCheckSuggestionCrossProvider({
        creditCheck: applicantData?.credit_check,
        suggestion,
      }),
    tooltipKey: applicantData?.permissions?.can_screen
      ? null
      : 'RentalPortal.ApplicantCreditCheckNotAllowedTooltipKey',
  }
}

export function getApplicantCreditCheckSuggestionText({ suggestion, t }) {
  return t('RentalPortal.ApplicantCreditCheckSuggestionKey', {
    date: getApplicantShortDate(suggestion.arrived_at),
  })
}

export function getApplicantCreditCheckSuggestionProviderText({
  creditCheck,
  suggestion,
  t,
}) {
  if (!suggestion.detected_provider_display_name) {
    return t('RentalPortal.ApplicantCreditCheckSuggestionUnknownProviderKey')
  }

  return t(
    getIsApplicantCreditCheckSuggestionCrossProvider({
      creditCheck,
      suggestion,
    })
      ? 'RentalPortal.ApplicantCreditCheckSuggestionCrossProviderKey'
      : 'RentalPortal.ApplicantCreditCheckSuggestionProviderKey',
    { provider: suggestion.detected_provider_display_name }
  )
}

export function getApplicantCreditCheckSuggestionFileProps(file) {
  return { file: { mime_type: file?.mime } }
}

export function getIsApplicantCreditCheckUploadAction(action) {
  return APPLICANT_CREDIT_CHECK_UPLOAD_ACTIONS.includes(action)
}

export function getIsApplicantCreditCheckPrimaryAction(action) {
  return APPLICANT_CREDIT_CHECK_PRIMARY_ACTIONS.includes(action)
}

export function getIsApplicantCreditCheckCorrection(action) {
  return action === APPLICANT_CREDIT_CHECK_ACTION.correctVerdict
}

export function getApplicantCreditCheckGateNotifications({ hasError, t }) {
  return hasError
    ? [
        {
          id: APPLICANT_CREDIT_CHECK_REQUEST_ERROR_NOTIFICATION_ID,
          text: t('RentalPortal.ApplicantCreditCheckRequestErrorKey'),
          type: NOTIFICATION_TYPE.error,
        },
      ]
    : EMPTY_LIST
}
